import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GenomePanel } from "@/components/ui/GenomePanel";
import { PageBanner } from "@/components/ui/PageBanner";
import { PersonaliseCard, ClubBanner, GOALS } from "@/components/ui/Personalise";
import { TodayCard } from "@/components/perform/TodayCard";
import { FeedbackCard } from "@/components/perform/FeedbackVoice";
import { NextClassCard } from "@/components/perform/Timetable";
import { IMG } from "@/lib/assets";
import { Dumbbell, LineChart, CalendarDays, Sparkles, Activity, ArrowRight, Flame, Wand2, Loader2 } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: summary } = useQuery({ queryKey: ["summary"], queryFn: async () => (await api.get("/api/me/summary")).data });
  const { data: genome } = useQuery({ queryKey: ["genome", null], queryFn: async () => (await api.get("/api/ava/genome")).data });
  useEffect(() => {
    let club = "";
    try { club = localStorage.getItem("octon_club_code") || ""; } catch (_) {}
    if (club && user && !user.club_id) navigate(`/app/membership?club=${club}`);
  }, [user]); // eslint-disable-line
  const { data: streak } = useQuery({ queryKey: ["streak"], queryFn: async () => (await api.get("/api/streak")).data });

  const stats = [
    { label: "Assigned workouts", value: summary?.workouts ?? "–", icon: Dumbbell, to: "/app/workouts", c: "#CCFF00" },
    { label: "Sessions logged", value: summary?.sessions_logged ?? "–", icon: Activity, to: "/app/progress", c: "#3D7BFF" },
    { label: "Upcoming classes", value: summary?.upcoming_classes ?? "–", icon: CalendarDays, to: "/app/schedule", c: "#FFB340" },
    { label: "Coach reviews", value: summary?.cycles ?? "–", icon: Sparkles, to: "/app/coach", c: "#B57BFF" },
  ];
  const tiles = [
    { img: IMG.strength, title: "OCTON Personal Trainer", tag: "Live camera coaching", to: "/app/train?mode=gym" },
    { img: IMG.progress, title: "Training Areas", tag: "Strength to gymnastics", to: "/app/areas" },
    { img: IMG.yoga, title: "Warm-up & Stretch", tag: "Before and after training", to: "/app/warmup" },
    { img: IMG.nutrition, title: "Nutrition", tag: "Diet plan for your goals", to: "/app/nutrition" },
    { img: IMG.yoga, title: "Mind & Flow", tag: "Yoga and breathwork", to: "/app/mind" },
  ];

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.landingHero} height="h-64 md:h-72" overline="today"
        title={`Welcome back, ${user?.name?.split(" ")[0] || "athlete"}.`}
        subtitle={user?.prefs?.goal ? `Goal: ${GOALS.find((g) => g.key === user.prefs.goal)?.label}. ${user.prefs.days_per_week || 3} sessions a week. Your OCTON coach builds on every one of them.` : "Your OCTON coach remembers how you train and builds on it every session. Let's train."}
        actions={
          <button onClick={() => navigate("/app/coach")} data-testid="dash-coach-cta"
            className="inline-flex items-center gap-2 bg-acid text-black hover:bg-[#AACC00] transition-colors rounded-full h-11 px-6 font-medium shadow-[0_0_30px_rgba(204,255,0,0.35)]">
            <Sparkles className="w-4 h-4" /> Train with OCTON
          </button>} />

      <div className="flex justify-end -mt-2"><PersonaliseCard /></div>
      <ClubBanner />
      {["owner", "coach"].includes(user?.club_role) && user?.club_active ? (
        <button onClick={() => navigate("/app/club")} data-testid="go-club-home" className="w-full surface rounded-2xl p-4 text-left flex items-center justify-between gap-3 border border-acid/30">
          <span><span className="mono-label text-acid block">you run {user?.club_name || "your club"}</span><span className="font-semibold">Open your club home: timetable, programming, members and feedback</span></span>
          <ArrowRight className="w-5 h-5 text-acid shrink-0" />
        </button>
      ) : <NextClassCard />}
      <TodayCard />
      <FeedbackCard />
      <PlanChooser user={user} navigate={navigate} />
      <GettingStarted user={user} summary={summary} navigate={navigate} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <motion.button key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            whileHover={{ y: -4 }} onClick={() => navigate(s.to)} data-testid={`stat-${i}`}
            className="relative overflow-hidden rounded-xl p-5 text-left border border-[#1E222A] bg-[#0F1115] group"
            style={{ boxShadow: `inset 0 1px 0 ${s.c}22` }}>
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl opacity-25 group-hover:opacity-50 transition-opacity" style={{ background: s.c }} />
            <div className="relative flex items-center justify-between mb-4">
              <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${s.c}1F`, border: `1px solid ${s.c}55` }}>
                <s.icon className="w-5 h-5" style={{ color: s.c }} />
              </span>
              <ArrowRight className="w-4 h-4 text-[#565C6F] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </div>
            <p className="relative font-heading font-black text-4xl tracking-tight">{s.value}</p>
            <p className="relative mono-label mt-1">{s.label}</p>
          </motion.button>
        ))}
      </div>

      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <span className="mono-label text-acid">pick up where you left off</span>
            <h2 className="font-heading font-black text-2xl tracking-tight mt-1">Train today</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {tiles.map((t, i) => (
            <motion.button key={t.title} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.07 }}
              onClick={() => navigate(t.to)} data-testid={`tile-${i}`}
              className="relative rounded-xl overflow-hidden aspect-[4/5] md:aspect-[3/4] group text-left">
              <img src={t.img} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(0deg,#050505 15%,rgba(5,5,5,0.55) 45%,rgba(5,5,5,0.05) 75%)" }} />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 group-hover:ring-acid/60 rounded-xl transition-all" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="mono-label text-acid drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">{t.tag}</p>
                <p className="font-heading font-black text-xl tracking-tight flex items-center gap-1.5">{t.title}
                  <ArrowRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" /></p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          {streak?.at_risk && (
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
              className="surface rounded-lg p-4 flex items-center gap-3 border border-[#FFB340]/40" data-testid="streak-at-risk">
              <Flame className="w-5 h-5 text-[#FFB340] shrink-0" />
              <p className="text-sm text-white/90 flex-1">{streak.message}</p>
              <button onClick={() => navigate("/app/coach")} className="bg-[#FFB340] text-black rounded-full px-4 h-9 text-sm font-medium shrink-0" data-testid="streak-log-cta">Log now</button>
            </motion.div>
          )}
          <NextSession navigate={navigate} />
        </div>
        <div className="lg:col-span-4 space-y-6">
          <StreakCard streak={streak} />
          {user?.role === "admin" && <GenomePanel genome={genome} />}
        </div>
      </div>
    </div>
  );
}

function PlanChooser({ user, navigate }) {
  if (!user || user.role !== "client" || user.membership_status === "active" || user.club_id) return null;
  const opts = [
    { key: "individual", title: "For me", sub: "Individual athlete", price: "£10 a month", img: IMG.strength, c: "#CCFF00",
      points: ["Your own OCTON Coach by chat and voice", "Training Diary, warm-ups and progress profile", "Nutrition and Calm & Recovery"],
      cta: "Choose Individual", to: "/app/membership?plan=individual_monthly" },
    { key: "club", title: "For my club", sub: "Clubs, gyms and teams", price: "£99 a month · up to 30 athletes", img: IMG.conditioning, c: "#3D7BFF",
      points: ["Everything in Individual for your whole squad", "Club dashboard: roster, consistency, PRs", "Club insights and quiet-athlete alerts"],
      cta: "Set up my club", to: "/app/membership?plan=club_monthly" },
  ];
  return (
    <div data-testid="plan-chooser">
      <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
        <div>
          <span className="mono-label text-acid">how will you train with OCTON?</span>
          <h2 className="font-heading font-black text-2xl tracking-tight mt-1">Choose your path</h2>
        </div>
        <button onClick={() => navigate("/app/membership")} className="text-sm text-acid hover:underline">Have a club code? Join your club</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {opts.map((o, i) => (
          <motion.button key={o.key} onClick={() => navigate(o.to)} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} whileHover={{ y: -4 }}
            className="group relative overflow-hidden rounded-2xl border text-left min-h-[16rem] flex" style={{ borderColor: `${o.c}55` }} data-testid={`choose-${o.key}`}>
            <img src={o.img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#050505 20%,rgba(5,5,5,0.6) 70%,rgba(5,5,5,0.3))" }} />
            <div className="relative p-6 flex flex-col justify-between w-full">
              <div>
                <p className="mono-label" style={{ color: o.c }}>{o.sub}</p>
                <p className="font-heading font-black text-3xl tracking-tight mt-1">{o.title}</p>
                <p className="font-heading font-bold mt-1" style={{ color: o.c }}>{o.price}</p>
                <ul className="mt-3 space-y-1 text-sm text-white/85">{o.points.map((p) => <li key={p}>✓ {p}</li>)}</ul>
              </div>
              <span className="mt-4 inline-flex items-center gap-2 self-start rounded-full px-5 h-10 font-medium text-black" style={{ background: o.c }}>
                {o.cta} <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

function GettingStarted({ user, summary, navigate }) {
  const isClient = user?.role === "client";
  const trialing = user?.membership_status === "trialing" && !(user?.club_id && user?.club_active);
  const daysLeft = user?.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000)) : null;
  const fresh = (summary?.sessions_logged ?? 0) === 0;
  if (!isClient || (!fresh && !trialing)) return null;
  const steps = [
    { n: 1, title: "Tell your coach your goal", body: "Say what you want to achieve and what you trained last. It logs everything for you.", cta: "Talk to your coach", to: "/app/coach", img: IMG.strength },
    { n: 2, title: "Try a live camera session", body: "Prop up your phone or laptop and your coach counts reps and checks form.", cta: "Start a session", to: "/app/train?mode=gym", img: IMG.conditioning },
    { n: 3, title: "Get your diet plan", body: "Add your height, weight and goal for a full day of meals built around you.", cta: "Build my plan", to: "/app/nutrition", img: IMG.nutrition },
  ];
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-xl border border-acid/30 bg-gradient-to-br from-[#12160A] via-[#0D1009] to-[#0B0D11] p-5 md:p-6" data-testid="getting-started">
      <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: "#CCFF00" }} />
      <div className="relative flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <span className="mono-label text-acid">get started</span>
          <h2 className="font-heading font-black text-2xl tracking-tight mt-1">Three steps to your first win</h2>
        </div>
        {trialing && daysLeft !== null && (
          <button onClick={() => navigate("/app/membership")} className="mono-label rounded-full border border-acid/50 text-acid px-3 py-1.5 hover:bg-acid hover:text-black transition-colors">
            {daysLeft} day{daysLeft === 1 ? "" : "s"} left of your free taster · see plans
          </button>
        )}
      </div>
      <div className="relative grid grid-cols-1 md:grid-cols-3 gap-3">
        {steps.map((st) => (
          <button key={st.n} onClick={() => navigate(st.to)} className="group text-left rounded-lg overflow-hidden border border-[#1E222A] bg-[#0F1115] hover:border-acid/60 transition-colors">
            <div className="relative h-24 overflow-hidden">
              <img src={st.img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(0deg,#0F1115,transparent)" }} />
              <span className="absolute top-2 left-2 w-7 h-7 rounded-full bg-acid text-black font-heading font-black flex items-center justify-center text-sm">{st.n}</span>
            </div>
            <div className="p-4 pt-2">
              <p className="font-heading font-bold">{st.title}</p>
              <p className="text-xs text-[#8A91A6] mt-1">{st.body}</p>
              <p className="text-sm text-acid mt-3 inline-flex items-center gap-1">{st.cta} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></p>
            </div>
          </button>
        ))}
      </div>
    </motion.div>
  );
}

function StreakCard({ streak }) {
  const s = streak || {};
  const cur = s.current_streak ?? 0;
  const goal = 7;
  const pct = Math.min(cur / goal, 1);
  const R = 34, C = 2 * Math.PI * R;
  return (
    <div className="relative overflow-hidden rounded-xl p-5 border border-[#1E222A] bg-[#0F1115]" data-testid="streak-card">
      <div className="absolute -bottom-16 -left-10 w-48 h-48 rounded-full blur-3xl opacity-20" style={{ background: cur > 0 ? "#CCFF00" : "#3D7BFF" }} />
      <div className="relative flex items-center gap-2 mb-4">
        <Flame className={`w-4 h-4 ${s.active_today ? "text-acid" : "text-[#FFB340]"}`} />
        <span className="font-heading font-bold tracking-tight">Training streak</span>
      </div>
      <div className="relative flex items-center gap-5">
        <svg width="84" height="84" viewBox="0 0 84 84" className="shrink-0" aria-hidden="true">
          <circle cx="42" cy="42" r={R} stroke="#1E222A" strokeWidth="7" fill="none" />
          <motion.circle cx="42" cy="42" r={R} stroke="#CCFF00" strokeWidth="7" fill="none" strokeLinecap="round"
            strokeDasharray={C} initial={{ strokeDashoffset: C }} animate={{ strokeDashoffset: C * (1 - pct) }}
            transition={{ duration: 1.2, ease: "easeOut" }} transform="rotate(-90 42 42)"
            style={{ filter: "drop-shadow(0 0 6px rgba(204,255,0,0.6))" }} />
          <text x="42" y="49" textAnchor="middle" className="font-heading" fontWeight="900" fontSize="22" fill={cur > 0 ? "#CCFF00" : "#8A91A6"}>{cur}</text>
        </svg>
        <div className="flex-1">
          <p className="text-white/90 text-sm">day{cur === 1 ? "" : "s"} in a row</p>
          <p className="mono-label mt-1">goal {goal} · longest {s.longest_streak ?? 0}</p>
        </div>
      </div>
      {s.message && <p className="relative text-xs text-[#8A91A6] mt-4">{s.message}</p>}
    </div>
  );
}

function NextSession({ navigate }) {
  const qc = useQueryClient();
  const { data: program } = useQuery({ queryKey: ["next-program"], queryFn: async () => (await api.get("/api/program/next")).data });
  const gen = useMutation({
    mutationFn: async () => (await api.post("/api/program/next")).data,
    onSuccess: (d) => { qc.setQueryData(["next-program"], d); toast.success("OCTON programmed your next session"); },
    onError: (e) => toast.error(apiErr(e, "Couldn't generate a program")),
  });
  const blocks = Array.isArray(program?.blocks) ? program.blocks : [];
  return (
    <div className="relative isolate overflow-hidden rounded-xl p-6 border border-[#1E222A] bg-gradient-to-br from-[#10131A] to-[#0B0D11]" data-testid="next-session-card">
      <div className="absolute -z-10 -top-20 right-0 w-64 h-64 rounded-full blur-3xl opacity-15 pointer-events-none" style={{ background: "#3D7BFF" }} />
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2"><Wand2 className="w-5 h-5 text-neural" /><span className="mono-label text-neural">octon · progressive overload</span></div>
          <h3 className="font-heading font-bold text-2xl tracking-tight mt-2">{program?.title || "Your next session"}</h3>
          {program?.focus && <p className="text-[#8A91A6] text-sm mt-1 max-w-lg">{program.focus}</p>}
        </div>
        <button onClick={() => gen.mutate()} disabled={gen.isPending} data-testid="generate-program-btn"
          className="bg-acid text-black hover:bg-[#AACC00] transition-colors rounded-full h-10 px-5 text-sm font-medium inline-flex items-center gap-2 shrink-0 disabled:opacity-60">
          {gen.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Programming…</> : <><Wand2 className="w-4 h-4" /> {blocks.length ? "Regenerate" : "Generate"}</>}
        </button>
      </div>
      {blocks.length === 0 ? (
        <p className="text-[#565C6F] text-sm py-6 text-center">Log a few sessions with OCTON, then let it build your next progressive-overload workout from your trends.</p>
      ) : (
        <div className="space-y-2">
          {blocks.map((b, i) => (
            <div key={`${b.movement}-${b.scheme}`} className="rounded-md border border-[#1E222A] surface-elev p-3 flex items-start gap-3" data-testid="program-block">
              <span className="font-heading font-black text-lg text-[#565C6F] w-6 shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-white/90">{b.movement}</span>
                  <span className="mono-label px-2 py-0.5 rounded bg-white/5">{b.scheme}</span>
                  <span className="font-mono2 text-sm text-acid">{b.target}</span>
                </div>
                {b.rationale && <p className="text-xs text-[#8A91A6] mt-1">{b.rationale}</p>}
              </div>
            </div>
          ))}
          {program?.notes && <p className="text-xs text-[#8A91A6] italic mt-3 flex items-start gap-1"><Sparkles className="w-3 h-3 mt-0.5 text-neural shrink-0" />{program.notes}</p>}
        </div>
      )}
    </div>
  );
}
